import React, { ComponentProps, useState } from 'react';
import clsx from 'clsx';
import PasswordInput from '../../../ui/PasswordInput';
function calculatePasswordStrength(password: string): number {
  let score = 0;
  if (!password) return score;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score;
}
type Props = ComponentProps<typeof PasswordInput>;
const PasswordStrengthLevels = ['weak', 'moderate', 'strong', 'very strong'];

const PasswordInputWithStrengthLevel = React.memo(
  React.forwardRef<any, Props>(function ({ onChange, ...props }: Props, ref) {
    const [strengthLevel, setStrengthLevel] = useState<number>(0);
    const gaugeWrapper = clsx(
      'strength-level-bar',
      strengthLevel === 1 && 'weak',
      strengthLevel === 2 && 'moderate',
      strengthLevel === 3 && 'strong',
      strengthLevel === 4 && 'very-strong'
    );

    // keep strength in sync with controlled value (handles reset())
    React.useEffect(() => {
      const val = (props as any).value ?? '';
      setStrengthLevel(calculatePasswordStrength(String(val)));
    }, [(props as any).value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const entry = e.target.value;
      const strength = calculatePasswordStrength(entry);
      setStrengthLevel(strength);
      if (onChange)
        (onChange as unknown as React.ChangeEventHandler<HTMLInputElement>)(e);
    };

    return (
      <div className="space-y-2" ref={ref}>
        <PasswordInput
          {...props}
          value={props?.value}
          type="password"
          onChange={handleChange}
        />
        {/* strength level bar */}
        <div className={gaugeWrapper}>
          <span></span>
          <span></span>
          <span></span>
          <span></span>
        </div>
        <small className="medium-emphasis-text space-x-1">
          <span>Password strength:</span>
          <span className="font-semibold capitalize">
            {PasswordStrengthLevels[strengthLevel - 1] || 'weak'}
          </span>
        </small>
      </div>
    );
  }),
  (prev, next) => {
    return prev.value === next.value;
  }
);

export default PasswordInputWithStrengthLevel;
